import { useCallback, useEffect } from 'react';
import { isMobile } from '@elfsight/widget-utils';
import type { A11yState } from '../../types';
import { WIDGET_CLASS } from '../constants';

const FOCUSABLE_SELECTORS = [
  'a[href]',
  'button:not([disabled])',
  'input:not([type="hidden"]):not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])'
];

export function useFocusFirstVisibleElement({ mode }: A11yState) {
  const applyKeyboardNavigation = mode === 'keyboard-navigation';

  const isMobileDevice = isMobile();

  const focusFirstElement = useCallback(() => {
    const isVisibleAndFocusable = (element: HTMLElement): boolean => {
      const rect = element.getBoundingClientRect();
      const style = getComputedStyle(element);

      const isVisible =
        rect.width > 0 &&
        rect.height > 0 &&
        rect.top >= 0 &&
        rect.top < window.innerHeight &&
        style.visibility !== 'hidden' &&
        style.display !== 'none' &&
        !element.hasAttribute('disabled');

      const isInsideWidget = element.classList.value.includes(WIDGET_CLASS);
      const isAriaHidden = element.closest('[aria-hidden="true"]') !== null;

      return isVisible && !isInsideWidget && !isAriaHidden;
    };

    const elements = Array.from(
      document.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTORS.join(','))
    ).filter(isVisibleAndFocusable);

    if (elements.length > 0) {
      elements[0].focus();
    }
  }, []);

  useEffect(() => {
    if (applyKeyboardNavigation && !isMobileDevice) {
      focusFirstElement();
    }
  }, [applyKeyboardNavigation, isMobileDevice, focusFirstElement]);
}
